import { YesNoAnswer } from 'scenes/common/types';
import {
  WireGender,
  WirePartyType,
  WirePolicyRole,
} from 'scenes/common/api/quote/types';
import {
  DriverGender,
  MotorFunnelState,
  Driver,
  PolicyHolder,
} from 'scenes/motor/types';
import { toDDMMYYYY, toYYYYMMDD } from 'scenes/common/helpers/datetime';
import {
  getBusinessContactName,
  getBusinessNameAndAbn,
  getContactEmailAddress,
  getContactPhoneNumber,
  getPrimaryPolicyHolderPersonalContact,
  hasBusinessRegisteredForGst,
  getSelectedPolicyHolders,
  isPolicyIssuedToBusiness,
} from 'scenes/motor/selectors';
import {
  ADDRESS_ID,
  partyId,
  PRIMARY_PARTY,
} from 'scenes/common/api/quote/marshaller/ids';

import { WireMotorQuotePayload, WireMotorParty } from '../types';

const toWireGender = (gender: DriverGender) => {
  switch (gender) {
    case DriverGender.Male:
      return WireGender.Male;
    case DriverGender.Female:
      return WireGender.Female;
    default:
      return WireGender.Unknown;
  }
};

const mapDriver = (driver: Driver, id: string) => ({
  id,
  addressRefId: [ADDRESS_ID],
  type:
    id === PRIMARY_PARTY
      ? WirePartyType.Driver
      : WirePartyType.AdditionalDriver,
  name: `${driver.firstName.trim()} ${driver.lastName.trim()}`,
  lastName: driver.lastName.trim(),
  firstName: driver.firstName.trim(),
  middleName: '',
  company: '',
  dob: toYYYYMMDD(driver.dob),
  gender: toWireGender(driver.gender),
  role:
    id === PRIMARY_PARTY
      ? WirePolicyRole.PrimaryPolicyHolder
      : WirePolicyRole.PolicyHolder,
  otherAttributes: [
    { name: 'CLAIMSLAST3YRS', value: driver.claimsMade.toString() },
    {
      name: 'ATFAULTCLAIMS',
      value:
        driver.claimsMade > 0 && driver.payableClaims > 0
          ? driver.payableClaims.toString()
          : '0',
    },
    {
      name: 'LicenseTerm',
      value: driver.yearsDriving.toString(),
    },
  ],
});

const mapPolicyHolder = (policyHolder: PolicyHolder, id: string) => ({
  id,
  addressRefId: [ADDRESS_ID],
  type:
    id === PRIMARY_PARTY
      ? WirePartyType.PolicyHolder
      : WirePartyType.AdditionalPolicyHolder,
  name: `${policyHolder.firstName.trim()} ${policyHolder.lastName.trim()}`,
  lastName: policyHolder.lastName.trim(),
  firstName: policyHolder.firstName.trim(),
  middleName: '',
  company: '',
  dob: toYYYYMMDD(policyHolder.dob),
  gender: toWireGender(policyHolder.gender),
  role: WirePolicyRole.PolicyHolder,
  otherAttributes: [],
});

const filterDrivers = (state: MotorFunnelState) => (
  policyHolder: PolicyHolder
): boolean =>
  ![state.quoteDetails, ...state.quoteDetails.additionalDrivers].find(
    (driver) =>
      driver.firstName === policyHolder.firstName &&
      driver.lastName === policyHolder.lastName
  );

const mapCompany = (state: MotorFunnelState): WireMotorParty[] => {
  const businessContact = getBusinessContactName(state);
  const { businessName, abn } = getBusinessNameAndAbn(state);
  return isPolicyIssuedToBusiness(state)
    ? [
        {
          id: 'Company',
          addressRefId: [ADDRESS_ID],
          type: WirePartyType.Company,
          name: `${businessContact.firstName.trim()} ${businessContact.lastName.trim()}`,
          lastName: businessContact.lastName.trim(),
          firstName: businessContact.firstName.trim(),
          middleName: '',
          company: businessName.trim(),
          dob: '',
          gender: WireGender.NA,
          role: WirePolicyRole.PrimaryPolicyHolder,
          otherAttributes: [
            ...(abn
              ? [
                  {
                    name: 'ABN',
                    value: abn,
                  },
                ]
              : []),
            {
              name: 'TaxExemptStatus',
              value: hasBusinessRegisteredForGst(state) ? 'true' : 'false',
            },
          ],
        },
      ]
    : [];
};

export const toWireMotorParties = (
  state: MotorFunnelState
): WireMotorParty[] => [
  mapDriver(state.quoteDetails, PRIMARY_PARTY),
  ...state.quoteDetails.additionalDrivers.map((driver, idx) =>
    mapDriver(driver, partyId(idx))
  ),
  ...state.policyDetails.policyHolders
    .filter(filterDrivers(state))
    .map((policyHolder, idx) =>
      mapPolicyHolder(
        policyHolder,
        partyId(idx + state.quoteDetails.additionalDrivers.length)
      )
    ),
];

export const vehicleRefPartyIds = (state: MotorFunnelState): string[] => [
  PRIMARY_PARTY,
  ...state.quoteDetails.additionalDrivers.map((_, idx) => partyId(idx)),
];

export const policyRefPartyIds = (state: MotorFunnelState): string[] => [
  ...toWireMotorParties(state).map((party) => party.id),
];

const mapNonPrimaryPolicyHolderRole = (
  party: WireMotorParty,
  selectedPolicyHolders: PolicyHolder[]
): WirePolicyRole => {
  const isSelectedPolicyHolder = selectedPolicyHolders.find(
    (ph) => party.firstName === ph.firstName && party.lastName === ph.lastName
  );

  if (isSelectedPolicyHolder) {
    return WirePolicyRole.PolicyHolder;
  }
  return WirePolicyRole.NotApplicable;
};

export const toPolicyDetailsParties = (state: MotorFunnelState) => {
  const parties = [...mapCompany(state), ...toWireMotorParties(state)];

  const isPrimaryPolicyHolder = (party: WireMotorParty): boolean => {
    const primaryContact = getPrimaryPolicyHolderPersonalContact(state);
    return (
      primaryContact.personalContactFirstName === party.firstName &&
      primaryContact.personalContactLastName === party.lastName
    );
  };

  const primaryParty = parties.find(isPrimaryPolicyHolder) || parties[0];

  return parties.map((party) => ({
    ...party,
    role:
      primaryParty.id === party.id
        ? WirePolicyRole.PrimaryPolicyHolder
        : mapNonPrimaryPolicyHolderRole(party, getSelectedPolicyHolders(state)),
    email:
      primaryParty.id === party.id ? getContactEmailAddress(state).trim() : '',
    mobile: primaryParty.id === party.id ? getContactPhoneNumber(state) : '',
  }));
};

const toDriverGender = (party: WireMotorParty): DriverGender => {
  switch (party.gender) {
    case WireGender.Male:
      return DriverGender.Male;
    case WireGender.Female:
      return DriverGender.Female;
    default:
      return DriverGender.Unspecified;
  }
};

const toYearsDriving = (party: WireMotorParty): number =>
  parseInt(
    party.otherAttributes.find((attr) => attr.name === 'LicenseTerm')?.value ??
      '0',
    10
  );

const toClaimsMade = (party: WireMotorParty): number =>
  parseInt(
    party.otherAttributes.find((attr) => attr.name === 'CLAIMSLAST3YRS')
      ?.value ?? '0',
    10
  );

const toPayableClaims = (party: WireMotorParty): number =>
  parseInt(
    party.otherAttributes.find((attr) => attr.name === 'ATFAULTCLAIMS')
      ?.value ?? '0',
    10
  );

const toDriver = (party: WireMotorParty): Driver => ({
  firstName: '',
  lastName: '',
  gender: toDriverGender(party),
  dob: toDDMMYYYY(party.dob),
  yearsDriving: toYearsDriving(party),
  claimsMade: toClaimsMade(party),
  payableClaims: toPayableClaims(party),
});

const getMainDriver = (quote: WireMotorQuotePayload): WireMotorParty =>
  quote.parties.find((p) => p.type === WirePartyType.Driver) as WireMotorParty;

const getAdditionalDrivers = (quote: WireMotorQuotePayload): WireMotorParty[] =>
  quote.parties.filter(
    (party) => party.type === WirePartyType.AdditionalDriver
  );

export const mapMainDriver = (quote: WireMotorQuotePayload): Driver =>
  toDriver(getMainDriver(quote));

export const toAdditionalDrivers = (quote: WireMotorQuotePayload): Driver[] =>
  getAdditionalDrivers(quote).map((party) => toDriver(party));

export const toAddDriver = (quote: WireMotorQuotePayload): YesNoAnswer =>
  getAdditionalDrivers(quote).length > 0 ? YesNoAnswer.Yes : YesNoAnswer.No;
